import { motion } from "framer-motion";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";
import Gallery from "@/components/Gallery";
import { MaskedLine, Reveal, SectionTag } from "@/components/Reveal";
import { EXPERIENCES, SMILE_TALK } from "@/data";

const Block = ({ label, children, dark = false }) => (
  <Reveal>
    <div className="grid gap-4 border-t border-ink/10 py-10 md:grid-cols-12 md:gap-8">
      <span className={`font-display text-[10px] font-bold uppercase tracking-[0.35em] ${dark ? "text-ember" : "text-burgundy"} md:col-span-3`}>
        {label}
      </span>
      <div className="md:col-span-9">{children}</div>
    </div>
  </Reveal>
);

const SmileTalk = () => (
  <section className="bg-ink px-6 py-24 text-paper md:px-12 md:py-32" data-testid="smile-talk-case-study">
    <SectionTag no="★" label="CASE STUDY" dark />
    <h2 className="mt-8 font-display text-5xl font-black uppercase tracking-tighter md:text-7xl">
      <MaskedLine delay={0.1}><span>{SMILE_TALK.title}</span></MaskedLine>
    </h2>
    <p className="mt-4 font-serif text-2xl italic text-ember md:text-3xl">{SMILE_TALK.subtitle}</p>
    <Reveal>
      <p className="mt-8 max-w-2xl leading-relaxed text-paper/70">{SMILE_TALK.description}</p>
    </Reveal>

    <div className="mt-16">
      {SMILE_TALK.flow.map((f, i) => (
        <Reveal key={f.step} delay={i * 0.06}>
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:gap-8" data-testid={`smile-talk-step-${i}`}>
            <div className="flex w-full items-center gap-6 border border-white/15 p-6 transition-colors duration-300 hover:border-ember md:p-8">
              <span className="font-display text-3xl font-black text-ember md:text-5xl">{f.step}</span>
              <div>
                <p className="font-display text-sm font-black uppercase tracking-[0.15em] md:text-lg">{f.label}</p>
                <p className="mt-2 text-sm text-paper/60">{f.desc}</p>
              </div>
            </div>
            {i < SMILE_TALK.flow.length - 1 && (
              <ArrowDown className="ml-8 h-5 w-5 shrink-0 text-ember md:hidden" />
            )}
          </div>
          {i < SMILE_TALK.flow.length - 1 && <div className="mx-auto hidden h-6 w-px bg-ember/40 md:block" />}
        </Reveal>
      ))}
    </div>

    <div className="mt-20">
      <Gallery items={SMILE_TALK.gallery} dark title="SMILE TALK — DOKUMENTASI (EDITABLE)" />
      <p className="mt-6 text-xs text-paper/40">
        Slot untuk: Instagram screenshots, counselor content, booking interface, event documentation, dan short videos.
      </p>
    </div>
  </section>
);

const ExperienceDetail = () => {
  const { slug } = useParams();
  const idx = EXPERIENCES.findIndex((e) => e.slug === slug);
  if (idx === -1) return <Navigate to="/experiences" replace />;
  const exp = EXPERIENCES[idx];
  const prev = EXPERIENCES[(idx - 1 + EXPERIENCES.length) % EXPERIENCES.length];
  const next = EXPERIENCES[(idx + 1) % EXPERIENCES.length];

  return (
    <motion.main
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="pt-28"
      data-testid={`experience-detail-${exp.slug}`}
    >
      <header className="px-6 md:px-12">
        <Link to="/experiences" className="u-link inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.25em] text-ash" data-testid="back-to-experiences">
          <ArrowLeft className="h-4 w-4" /> All chapters
        </Link>
        <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 font-display text-[10px] font-bold uppercase tracking-[0.3em] text-burgundy md:text-xs">
          <span data-testid="exp-role">{exp.role}</span>
          <span className="text-ink/30">/</span>
          <span data-testid="exp-org">{exp.org}</span>
          <span className="text-ink/30">/</span>
          <span data-testid="exp-year">{exp.year || "[ADD YEAR]"}</span>
        </div>
        <h1 className="mt-6 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-6xl md:text-8xl" data-testid="exp-title">
          <MaskedLine delay={0.1}><span>{exp.title}</span></MaskedLine>
        </h1>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl font-serif text-2xl italic leading-relaxed text-burgundy md:text-3xl" data-testid="exp-hook">
            "{exp.hook}"
          </p>
          {exp.extra && (
            <p className="mt-4 inline-block border border-burgundy px-4 py-2 font-display text-xs font-bold uppercase tracking-[0.25em] text-burgundy" data-testid="exp-extra">
              {exp.extra}
            </p>
          )}
        </Reveal>
      </header>

      <section className="px-6 py-16 md:px-12">
        <Block label="What brought me here">
          <p className="max-w-2xl text-base leading-relaxed text-ink/80 md:text-lg">{exp.story}</p>
        </Block>
        <Block label="What I did">
          <ul className="max-w-2xl space-y-3">
            {exp.did.map((d, i) => (
              <li key={i} className="flex gap-4 text-base text-ink/80 md:text-lg">
                <span className="font-display text-xs font-bold text-burgundy">{String(i + 1).padStart(2, "0")}</span>
                {d}
              </li>
            ))}
          </ul>
        </Block>
        <Block label="What changed">
          <p className="max-w-2xl font-serif text-xl italic leading-relaxed text-ash md:text-2xl">{exp.learned}</p>
        </Block>
        {exp.mission && (
          <Block label="Why it matters">
            <p className="max-w-2xl text-base leading-relaxed text-ink/80 md:text-lg">{exp.mission}</p>
          </Block>
        )}
        <Block label="What I took with me">
          <p className="max-w-2xl font-display text-2xl font-black uppercase tracking-tight md:text-3xl">{exp.takeaway}</p>
        </Block>
        {exp.reflection && (
          <Block label="Reflection">
            <p className="max-w-2xl font-serif text-xl italic leading-relaxed text-burgundy md:text-2xl">"{exp.reflection}"</p>
          </Block>
        )}
      </section>

      {exp.caseStudy && <SmileTalk />}

      <section className={`px-6 py-20 md:px-12 ${exp.caseStudy ? "" : "bg-cream border-y border-ink/10"}`}>
        <Gallery items={exp.images} />
        {exp.videos?.length > 0 && (
          <div className="mt-14" data-testid="exp-videos">
            <p className="mb-6 font-display text-[10px] font-bold uppercase tracking-[0.35em] text-ink/50">ON VIDEO</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {exp.videos.map((v, i) => (
                <figure key={i} className="group relative overflow-hidden border border-ink/10 bg-ink" data-testid={`exp-video-${i}`}>
                  <video
                    src={v.src}
                    poster={v.poster}
                    controls
                    preload="none"
                    playsInline
                    data-cursor="PLAY"
                    className="max-h-[70vh] w-full object-contain"
                  />
                  {v.caption && (
                    <figcaption className="px-3 py-2 font-display text-[10px] uppercase tracking-[0.2em] text-paper/70">
                      {v.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        )}
      </section>

      <nav className="grid border-t border-ink/10 md:grid-cols-2" data-testid="exp-pagination">
        <Link to={`/experiences/${prev.slug}`} data-testid="exp-prev" data-cursor="OPEN" className="group flex items-center gap-4 border-b border-ink/10 p-8 transition-colors hover:bg-ink hover:text-paper md:border-b-0 md:border-r md:p-12">
          <ArrowLeft className="h-6 w-6 shrink-0 transition-transform group-hover:-translate-x-2" />
          <div>
            <span className="font-display text-[10px] uppercase tracking-[0.3em] text-burgundy">Previous chapter</span>
            <p className="font-display text-xl font-black uppercase tracking-tight md:text-2xl">{prev.title}</p>
          </div>
        </Link>
        <Link to={`/experiences/${next.slug}`} data-testid="exp-next" data-cursor="OPEN" className="group flex items-center justify-end gap-4 p-8 text-right transition-colors hover:bg-ink hover:text-paper md:p-12">
          <div>
            <span className="font-display text-[10px] uppercase tracking-[0.3em] text-burgundy">Next chapter</span>
            <p className="font-display text-xl font-black uppercase tracking-tight md:text-2xl">{next.title}</p>
          </div>
          <ArrowRight className="h-6 w-6 shrink-0 transition-transform group-hover:translate-x-2" />
        </Link>
      </nav>
    </motion.main>
  );
};

export default ExperienceDetail;
